import type { CommercialPlanData, PlanByMediaTypeItem, PlanByPeriodItem } from "../models/commericalPlanModels";
import { isDefinedNumber, sumNullable } from "./numberValueConversion";

export const getPlanPeriods = (planData: CommercialPlanData): PlanByPeriodItem[] =>
    planData.plan_by_retailer.flatMap(
        retailer => retailer.plan_by_period
    );

export const calculatePeriodRetailMediaSpend = (period: PlanByPeriodItem): number | null =>
    sumNullable(
        period.plan_by_media_type.map(
            (media: PlanByMediaTypeItem): number | null | undefined => media.media_type_spend
        )
    );

const calculateTotalVelocityChange = (period: PlanByPeriodItem): number | null => {
    if (!isDefinedNumber(period.promotion_velocity_change)) {
        return null;
    }

    const mediaVelocityChanges = period.plan_by_media_type.map(
        (media: PlanByMediaTypeItem): number | null | undefined => media.media_type_velocity_change
    );

    if (!mediaVelocityChanges.every(isDefinedNumber)) {
        return null;
    }

    return period.promotion_velocity_change
        + mediaVelocityChanges.reduce<number>(
            (total: number, velocityChange: number): number => total + velocityChange,
            0
        );
};

export const calculateExpectedVelocity = (period: PlanByPeriodItem, totalVelocityChange: number | null): number | null => {
    if (
        !isDefinedNumber(period.period_base_velocity)
        || !isDefinedNumber(period.number_stores ?? 1)
        || !isDefinedNumber(totalVelocityChange)
    ) {
        return null;
    }

    return period.period_base_velocity
        * (1 + totalVelocityChange)
        * (period.number_stores ?? 1);
};

export const calculateTotalExpectedVelocity = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map(period =>
            calculateExpectedVelocity(period, calculateTotalVelocityChange(period))
        )
    );

export const calculateProjectedRevenue = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map(period => {
            const totalVelocityChange = calculateTotalVelocityChange(period);
            const expectedVelocity = calculateExpectedVelocity(period, totalVelocityChange);

            if (!isDefinedNumber(period.promotion_price) || !isDefinedNumber(expectedVelocity)) {
                return null;
            }

            return period.promotion_price * expectedVelocity;
        })
    );

export const calculateProjectedProfit = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map(period => {
            const totalVelocityChange = calculateTotalVelocityChange(period);
            const expectedVelocity = calculateExpectedVelocity(period, totalVelocityChange);
            const retailMediaSpend = calculatePeriodRetailMediaSpend(period);

            if (
                !isDefinedNumber(expectedVelocity)
                || !isDefinedNumber(period.promotion_price)
                || !isDefinedNumber(period.cost_per_unit)
                || !isDefinedNumber(retailMediaSpend)
            ) {
                return null;
            }

            return expectedVelocity
                * (period.promotion_price - period.cost_per_unit)
                - retailMediaSpend;
        })
    );

export const calculateBaselineRevenue = (planData: CommercialPlanData): number | null =>
    sumNullable(getPlanPeriods(planData).map(period => {
        if (
            !isDefinedNumber(period.base_price)
            || !isDefinedNumber(period.period_base_velocity)
        ) {
            return null;
        }

        return period.base_price * period.period_base_velocity
            * (period.number_stores ?? 1);
    }));

export const calculateBaselineProfit = (planData: CommercialPlanData): number | null =>
    sumNullable(getPlanPeriods(planData).map(period => {
        if (
            !isDefinedNumber(period.base_price)
            || !isDefinedNumber(period.cost_per_unit)
            || !isDefinedNumber(period.period_base_velocity)
        ) {
            return null;
        }

        return (period.base_price - period.cost_per_unit)
            * period.period_base_velocity
            * (period.number_stores ?? 1);
    }));

export const calculateRetailMediaSpend = (planData: CommercialPlanData): number | null =>
    sumNullable(getPlanPeriods(planData).map(calculatePeriodRetailMediaSpend));

export const calculateTradeSpend = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map(period => {
            const totalVelocityChange = calculateTotalVelocityChange(period);
            const expectedVelocity = calculateExpectedVelocity(period, totalVelocityChange);

            if (
                !isDefinedNumber(period.base_price)
                || !isDefinedNumber(period.promotion_price)
                || !isDefinedNumber(expectedVelocity)
            ) {
                return null;
            }

            return (period.base_price - period.promotion_price) * expectedVelocity;
        })
    );

const calculatePercentageChange = (baseline: number | null, projected: number | null): number | null => {
    if (!isDefinedNumber(baseline) || !isDefinedNumber(projected) || baseline === 0) {
        return null;
    }

    return (projected - baseline) / baseline;
};

export type CommercialPlanMetrics = {
    projectedRevenue: number | null;
    projectedProfit: number | null;
    revenuePercentageChange: number | null;
    profitPercentageChange: number | null;
    roi: number | null;
};

export const calculateCommercialPlanMetrics = (planData: CommercialPlanData): CommercialPlanMetrics => {
    const projectedRevenue = calculateProjectedRevenue(planData);
    const projectedProfit = calculateProjectedProfit(planData);
    const baselineRevenue = calculateBaselineRevenue(planData);
    const baselineProfit = calculateBaselineProfit(planData);
    const tradeSpend = calculateTradeSpend(planData);
    const retailMediaSpend = calculateRetailMediaSpend(planData);
    const incrementalRevenue = isDefinedNumber(projectedRevenue) && isDefinedNumber(baselineRevenue)
        ? projectedRevenue - baselineRevenue
        : null;
    const totalRetailInvestment = isDefinedNumber(tradeSpend) && isDefinedNumber(retailMediaSpend)
        ? tradeSpend + retailMediaSpend
        : null;
    const roi = isDefinedNumber(incrementalRevenue)
        && isDefinedNumber(totalRetailInvestment)
        && totalRetailInvestment !== 0
        ? incrementalRevenue / totalRetailInvestment
        : null;

    return {
        projectedRevenue: projectedRevenue,
        projectedProfit: projectedProfit,
        revenuePercentageChange: calculatePercentageChange(baselineRevenue, projectedRevenue),
        profitPercentageChange: calculatePercentageChange(baselineProfit, projectedProfit),
        roi: roi
    };
};
